import type { components } from "../api/generated/survey-svc";
import { adaptAuthenticatedUser } from "../domain/authenticated-user/adapter";
import type { AuthenticatedUser } from "../domain/authenticated-user/model";

export type SessionStatus = "restoring" | "anonymous" | "authenticated";

export type SessionSnapshot = {
  status: SessionStatus;
  token: string | null;
  user: AuthenticatedUser | null;
  pendingEmail: string;
};

export type SessionDependencies = {
  restoreUser: () => Promise<components["schemas"]["AuthMe"] | undefined>;
  storage?: SessionStorage;
  pendingStorage?: SessionStorage;
  persistKey?: string;
};

export type SessionStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;
export type SessionUserUpdate = Omit<Partial<AuthenticatedUser>, "company"> & {
  company?: Partial<NonNullable<AuthenticatedUser["company"]>> | null;
};

type AuthenticatedCompany = NonNullable<AuthenticatedUser["company"]>;

const completeCompanyUpdate = (
  company: Partial<AuthenticatedCompany> | undefined,
): AuthenticatedCompany | null =>
  company &&
  typeof company.id === "string" &&
  company.id.length > 0 &&
  typeof company.name === "string" &&
  typeof company.paid === "boolean"
    ? {
        id: company.id,
        name: company.name,
        paid: company.paid,
        image: company.image ?? "",
        color: company.color ?? "",
      }
    : null;

const defaultStorage = () =>
  typeof localStorage === "undefined" ? undefined : localStorage;

const PENDING_EMAIL_KEY = "indicafix.pendingSecondFactorEmail";
const defaultPendingStorage = () =>
  typeof sessionStorage === "undefined" ? undefined : sessionStorage;
const readPendingEmail = (storage: SessionStorage | undefined) => {
  try {
    return storage?.getItem(PENDING_EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
};
const savePendingEmail = (
  storage: SessionStorage | undefined,
  email: string,
) => {
  try {
    if (email) storage?.setItem(PENDING_EMAIL_KEY, email);
    else storage?.removeItem(PENDING_EMAIL_KEY);
  } catch {
    // Authentication still works in memory when browser storage is unavailable.
  }
};

let activeToken: string | null = null;
let unauthorizedHandler: ((token: string) => void) | undefined;

export const getSessionToken = () =>
  activeToken ?? readPersistedToken(defaultStorage());
export const getTokenFromHeaders = (headers: unknown): string | null => {
  if (!headers || typeof headers !== "object") return null;
  const value = (headers as Record<string, unknown>).Authorization;
  return typeof value === "string" && value.startsWith("Bearer ")
    ? value.slice("Bearer ".length)
    : null;
};
export const setUnauthorizedHandler = (
  handler: ((token: string) => void) | undefined,
) => {
  unauthorizedHandler = handler;
};
export const invalidateAuthenticatedSession = (token: string) =>
  unauthorizedHandler?.(token);

const emptySnapshot = (): SessionSnapshot => ({
  status: "anonymous",
  token: null,
  user: null,
  pendingEmail: "",
});

export function readPersistedToken(
  storage: SessionStorage | undefined = defaultStorage(),
  key = "indicafixstore",
): string | null {
  if (!storage) return null;

  try {
    const persisted: unknown = JSON.parse(storage.getItem(key) ?? "null");
    if (!persisted || typeof persisted !== "object") return null;
    let session = (persisted as { session?: unknown }).session;
    if (typeof session === "string") {
      try {
        session = JSON.parse(session);
      } catch {
        return null;
      }
    }
    if (!session || typeof session !== "object") return null;
    const token = (session as { token?: unknown }).token;
    return typeof token === "string" && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

export function createSessionController(dependencies: SessionDependencies) {
  const storage = dependencies.storage ?? defaultStorage();
  const persistKey = dependencies.persistKey ?? "indicafixstore";
  const pendingStorage = dependencies.pendingStorage ?? defaultPendingStorage();
  let snapshot: SessionSnapshot = {
    ...emptySnapshot(),
    status: "restoring",
    token: readPersistedToken(storage, persistKey),
    pendingEmail: readPendingEmail(pendingStorage),
  };
  activeToken = snapshot.token;
  let generation = 0;
  let restoration: Promise<SessionSnapshot> | undefined;
  const listeners = new Set<() => void>();

  const notify = () => {
    listeners.forEach((listener) => {
      listener();
    });
  };
  const persist = (token: string | null) => {
    if (!storage) return;
    let persisted: Record<string, unknown> = {};
    try {
      const current = JSON.parse(storage.getItem(persistKey) ?? "null");
      if (current && typeof current === "object") persisted = current;
    } catch {
      persisted = {};
    }
    persisted.session = JSON.stringify({ token, signedIn: Boolean(token) });
    storage.setItem(persistKey, JSON.stringify(persisted));
  };
  const setSnapshot = (next: SessionSnapshot) => {
    snapshot = next;
    activeToken = next.token;
    notify();
  };
  const authenticate = (token: string, user: AuthenticatedUser) => {
    generation += 1;
    persist(token);
    savePendingEmail(pendingStorage, "");
    setSnapshot({
      ...snapshot,
      status: "authenticated",
      token,
      user,
      pendingEmail: "",
    });
  };
  const clear = () => {
    generation += 1;
    restoration = undefined;
    persist(null);
    savePendingEmail(pendingStorage, "");
    setSnapshot(emptySnapshot());
  };

  return {
    get snapshot() {
      return snapshot;
    },
    get token() {
      return snapshot.token;
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    authenticate,
    setPendingEmail(email: string) {
      savePendingEmail(pendingStorage, email);
      setSnapshot({ ...snapshot, pendingEmail: email });
    },
    updateUser(update: SessionUserUpdate, expectedToken?: string | null) {
      if (
        snapshot.user &&
        (!expectedToken || snapshot.token === expectedToken)
      ) {
        setSnapshot({
          ...snapshot,
          user: {
            ...snapshot.user,
            ...update,
            company:
              update.company === null
                ? null
                : update.company && snapshot.user.company
                  ? { ...snapshot.user.company, ...update.company }
                  : (completeCompanyUpdate(update.company) ??
                    snapshot.user.company),
          },
        });
      }
    },
    clear,
    clearIfToken(token: string) {
      if (snapshot.token === token) clear();
    },
    async restore() {
      if (restoration) return restoration;
      const requestedGeneration = generation;
      const token = snapshot.token;
      if (!token) {
        setSnapshot({
          ...emptySnapshot(),
          pendingEmail: snapshot.pendingEmail,
        });
        return snapshot;
      }

      const pending = dependencies
        .restoreUser()
        .then((response) => {
          if (requestedGeneration !== generation || snapshot.token !== token) {
            return snapshot;
          }
          if (!response) {
            clear();
            return snapshot;
          }
          const user = adaptAuthenticatedUser(response.currentUser);
          setSnapshot({ ...snapshot, status: "authenticated", token, user });
          return snapshot;
        })
        .catch((error: unknown) => {
          if (requestedGeneration === generation && snapshot.token === token) {
            console.error(
              "Session restoration failed; preserving the authenticated session.",
              error,
            );
            setSnapshot({ ...snapshot, status: "authenticated" });
          }
          return snapshot;
        })
        .finally(() => {
          if (restoration === pending) restoration = undefined;
        });
      restoration = pending;
      return pending;
    },
  };
}
